import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('portal.admin.researcher');
import { OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

@Component({
    selector: 'wiz-portal-admin-researcher',
template: templateSource || '',
    styles: [``],
})
export class PortalAdminResearcherComponent implements OnInit, OnChanges, OnDestroy {
    constructor(@Inject( Service) public service: Service) { }

    // 외부에서 전달받는 연구자 ID (비어있으면 목록, 있으면 상세)
    @Input() researcherId: string = '';

    // ─── 목록 모드 상태 ───
    public rows: any[] = [];
    public total: number = 0;
    public searchText: string = '';
    public searchField: string = 'name_kor';
    public page: number = 1;
    public dump: number = 10;
    public pages: number = 1;

    // 정렬
    public sortColumn: string = 'updated';
    public sortOrder: string = 'DESC';

    // 선택 삭제
    public selectedIds: Set<string> = new Set();
    public deleting: boolean = false;

    // 검색 필드 옵션
    public searchFieldOptions = [
        { value: 'name_kor', label: '이름(국문)' },
        { value: 'name_eng', label: '이름(영문)' },
        { value: 'researcher_id', label: '연구자 ID' },
        { value: 'email', label: '이메일' },
    ];

    // ─── 상세 모드 상태 ───
    public detail: any = null;
    public saving: boolean = false;
    public saveError: string = '';
    public saveSuccess: boolean = false;
    public detailLoading: boolean = false;
    public listLoading: boolean = false;

    private successTimer: any = null;

    public async ngOnInit() {
        await this.service.init();
        if (this.researcherId) {
            await this.loadDetail(this.researcherId);
        } else {
            await this.loadList(1);
        }
        await this.service.render();
    }

    public async ngOnChanges(changes: SimpleChanges) {
        if (changes['researcherId'] && !changes['researcherId'].firstChange) {
            const newId = changes['researcherId'].currentValue || '';
            if (newId) {
                await this.loadDetail(newId);
            } else {
                await this.loadList(1);
            }
            await this.service.render();
        }
    }

    public ngOnDestroy() {
        if (this.successTimer) clearTimeout(this.successTimer);
    }

    private apiUrl(fn: string) {
        return `/wiz/api/page.admin/${fn}`;
    }

    private async postForm(fn: string, params: Record<string, any>) {
        const body = new URLSearchParams();
        Object.entries(params || {}).forEach(([k, v]) => body.set(k, String(v ?? '')));
        const resp = await fetch(this.apiUrl(fn), {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            credentials: 'include',
            body: body.toString(),
        });
        return await resp.json();
    }

    // ═══════════════════════════════════════════════════════
    // 목록 모드
    // ═══════════════════════════════════════════════════════
    public async loadList(page: number = 1) {
        this.page = page;
        this.selectedIds.clear();
        this.listLoading = true;
        await this.service.render();
        try {
            const json = await this.postForm('researcher_list', {
                text: this.searchText,
                field: this.searchField,
                page: this.page,
                dump: this.dump,
                orderby: this.sortColumn,
                order: this.sortOrder,
            });
            if (json.code === 200) {
                this.rows = json?.data?.rows || [];
                this.total = json?.data?.total || 0;
            } else {
                this.rows = [];
                this.total = 0;
            }
        } catch (e) {
            this.rows = [];
            this.total = 0;
        }
        this.pages = Math.max(1, Math.ceil((this.total || 0) / this.dump));
        if (this.page > this.pages) this.page = this.pages;
        this.listLoading = false;
        await this.service.render();
    }

    public async onSearchKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            await this.loadList(1);
        }
    }

    public async toggleSort(column: string) {
        if (this.sortColumn === column) {
            this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.sortColumn = column;
            this.sortOrder = column === 'updated' ? 'DESC' : 'ASC';
        }
        await this.loadList(1);
    }

    public sortIcon(column: string): string {
        if (this.sortColumn !== column) return 'none';
        return this.sortOrder === 'ASC' ? 'asc' : 'desc';
    }

    // 페이지네이션
    public goToPage(page: number) {
        if (page < 1 || page > this.pages) return;
        this.loadList(page);
    }

    public goFirst() { this.goToPage(1); }
    public goPrev() { this.goToPage(this.page - 1); }
    public goNext() { this.goToPage(this.page + 1); }
    public goLast() { this.goToPage(this.pages); }

    public get pageNumbers(): number[] {
        const total = this.pages;
        const current = this.page;
        const blockSize = 10;
        const blockStart = Math.floor((current - 1) / blockSize) * blockSize + 1;
        const blockEnd = Math.min(blockStart + blockSize - 1, total);
        const pages: number[] = [];
        for (let i = blockStart; i <= blockEnd; i++) {
            pages.push(i);
        }
        return pages;
    }

    // 상세 페이지로 이동
    public goToDetail(researcherId: string) {
        this.service.href(`/admin/data/researcher/${researcherId}`);
    }

    // ═══════════════════════════════════════════════════════
    // 상세 모드
    // ═══════════════════════════════════════════════════════
    public async loadDetail(researcherId: string) {
        this.detailLoading = true;
        this.saveError = '';
        this.saveSuccess = false;
        await this.service.render();

        try {
            const json = await this.postForm('researcher_get', { researcher_id: researcherId });
            if (json.code === 200 && json.data?.data) {
                const row = json.data.data;
                this.detail = {
                    researcher_id: row?.researcher_id || '',
                    name_kor: row?.name_kor || '',
                    name_eng: row?.name_eng || '',
                    email: row?.email || '',
                    org_id: row?.org_id || '',
                    org_name: row?.org_name || '',
                    year: row?.year || 0,
                    paper_cnt: row?.paper_cnt || 0,
                    report_cnt: row?.report_cnt || 0,
                    patent_cnt: row?.patent_cnt || 0,
                    like_cnt: row?.like_cnt || 0,
                    keyword_kor: row?.keyword_kor || '',
                    keyword_eng: row?.keyword_eng || '',
                    created: row?.created || '',
                    updated: row?.updated || '',
                };
            } else {
                this.detail = null;
                this.saveError = json?.data?.message || '연구자 정보를 불러올 수 없습니다.';
            }
        } catch (e: any) {
            this.detail = null;
            this.saveError = e?.message || String(e);
        }

        this.detailLoading = false;
        await this.service.render();
    }

    public goToList() {
        this.service.href('/admin/data/researcher');
    }

    public sanitizeKeyword(value: string): string {
        if (!value) return '';
        return value
            .replace(/[,|\t\n]+/g, ';')
            .split(';')
            .map((s: string) => s.trim())
            .filter((s: string) => s.length > 0)
            .join('; ');
    }

    public async onKeywordBlur(field: string) {
        if (!this.detail) return;
        this.detail[field] = this.sanitizeKeyword(this.detail[field] || '');
        await this.service.render();
    }

    public async save() {
        if (!this.detail?.researcher_id || this.saving) return;
        if (!this.detail.name_kor?.trim()) {
            this.saveError = '이름(국문)은 필수 입력 항목입니다.';
            await this.service.render();
            return;
        }
        this.saving = true;
        this.saveError = '';
        this.saveSuccess = false;
        await this.service.render();

        const payload = {
            researcher_id: this.detail.researcher_id,
            name_kor: this.detail.name_kor,
            name_eng: this.detail.name_eng,
            email: this.detail.email,
            keyword_kor: this.detail.keyword_kor,
            keyword_eng: this.detail.keyword_eng,
        };

        try {
            const json = await this.postForm('researcher_update', payload);
            if (json.code !== 200) {
                this.saveError = json?.data?.message || '저장에 실패했습니다.';
            } else {
                this.saveSuccess = true;
                if (this.successTimer) clearTimeout(this.successTimer);
                this.successTimer = setTimeout(async () => {
                    this.saveSuccess = false;
                    await this.service.render();
                }, 3000);
                await this.loadDetail(this.detail.researcher_id);
            }
        } catch (e: any) {
            this.saveError = e?.message || String(e);
        }

        this.saving = false;
        await this.service.render();
    }

    // ═══════════════════════════════════════════════════════
    // 선택 삭제
    // ═══════════════════════════════════════════════════════
    public toggleSelect(researcherId: string) {
        if (this.selectedIds.has(researcherId)) {
            this.selectedIds.delete(researcherId);
        } else {
            this.selectedIds.add(researcherId);
        }
        this.service.render();
    }

    public toggleSelectAll() {
        if (this.selectedIds.size === this.rows.length && this.rows.length > 0) {
            this.selectedIds.clear();
        } else {
            this.selectedIds.clear();
            for (const r of this.rows) {
                this.selectedIds.add(r.researcher_id);
            }
        }
        this.service.render();
    }

    public isSelected(researcherId: string): boolean {
        return this.selectedIds.has(researcherId);
    }

    public get isAllSelected(): boolean {
        return this.rows.length > 0 && this.selectedIds.size === this.rows.length;
    }

    public async deleteSelected() {
        if (this.selectedIds.size === 0 || this.deleting) return;
        const count = this.selectedIds.size;
        if (!confirm(`선택한 ${count}건의 연구자 데이터를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;

        this.deleting = true;
        await this.service.render();

        try {
            const ids = Array.from(this.selectedIds);
            const json = await this.postForm('researcher_delete', { researcher_ids: JSON.stringify(ids) });
            if (json.code === 200) {
                this.selectedIds.clear();
                await this.loadList(this.page);
            } else {
                alert(json?.data?.message || '삭제에 실패했습니다.');
            }
        } catch (e: any) {
            alert(e?.message || '삭제 중 오류가 발생했습니다.');
        }

        this.deleting = false;
        await this.service.render();
    }

    // ═══════════════════════════════════════════════════════
    // 유틸리티
    // ═══════════════════════════════════════════════════════
    public formatNumber(n: number): string {
        if (!n && n !== 0) return '0';
        return n.toLocaleString();
    }

    public displayValue(val: any): string {
        if (val === null || val === undefined || val === '') return '-';
        return String(val);
    }
}

export default PortalAdminResearcherComponent;